import { SelectionModel } from '@angular/cdk/collections';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, MatSort, MatTableDataSource } from '@angular/material';
import { RolesService } from '@app/core/services/roles.service';
import { RolSingle } from '@app/shared/interfaces/rol';
import { AppConfig } from '@config/app.config';
import { GeneralsService } from '@core/services/generals.service';
import { RolesEditComponent } from '@modules/roles/roles-edit/roles-edit.component';
import { Constants } from '@shared/class/constants.class';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-roles-list',
  templateUrl: './roles-list.component.html'
})
export class RolesListComponent implements OnInit, OnDestroy {
  public constants = new Constants();

  public actions: Array<string> = [
    this.appConfig.ACTIONS.add.id,
    this.appConfig.ACTIONS.edit.id,
    this.appConfig.ACTIONS.delete.id,
    this.appConfig.ACTIONS.detail.id
  ];

  public location = 'roles';
  public dataSource: MatTableDataSource<RolSingle>;
  public selection = new SelectionModel<RolSingle>(true, []);
  public rolesList: Array<RolSingle> = [];
  public rol: RolSingle;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('filter') filterInput: ElementRef;

  constructor(
    public appConfig: AppConfig,
    public generalsService: GeneralsService,
    public rolesService: RolesService
  ) {
    this.constants.displayedColumns = [
      'id',
      'name',
      this.appConfig.ACTIONS.detail.id,
      this.appConfig.ACTIONS.edit.id,
      this.appConfig.ACTIONS.delete.id
    ];
  }

  ngOnInit() {
    this.generalsService.action$
      .pipe(takeUntil(this.constants.destroy$))
      .subscribe((result: any) => {
        if (result) {
          const actions = this.generalsService.getActions(
            this.constants.displayedColumns,
            this.actions,
            this.location
          );
          this.constants.displayedColumns = actions.displayedColumns;
          this.constants.displayedActions = actions.displayedActions;
          this.loadData();
        }
      });
  }

  ngOnDestroy() {
    this.constants.destroy$.next(true);
    this.constants.destroy$.unsubscribe();
  }

  loadData() {
    this.constants.loading = true;
    this.constants.elements = true;
    this.rolesService
      .getItems()
      .pipe(takeUntil(this.constants.destroy$))
      .subscribe(
        result => {
          this.rolesList = [];
          result.data.forEach((element: any) => {
            this.rol = { id: element.id, name: element.attributes.name };
            this.rolesList.push(this.rol);
          });
          if (this.rolesList.length) {
            this.dataSource = new MatTableDataSource(this.rolesList);
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
            this.constants.filter = this.dataSource.filteredData;
            this.selection.clear();
          } else {
            this.constants.elements = false;
          }

          this.constants.error = false;
          this.constants.loading = false;
        },
        error => {
          this.constants.errorText = this.generalsService.showError(error);
          this.constants.error = true;
          this.constants.loading = false;
        }
      );
  }

  reload() {
    this.generalsService.reload(this);
  }

  edit(action: string, item?: RolSingle) {
    let dialog: any;

    const data = {
      action: action,
      item: {}
    };

    this.constants.message = '';

    if (action === this.appConfig.ACTIONS.add.id) {
      this.constants.message = this.appConfig.LANG.message.addSuccess;
      dialog = this.generalsService.modal(
        RolesEditComponent,
        data,
        'mat-modal-big'
      );

      this.finishDialog(dialog);
    } else {
      const loading = this.generalsService.loading();
      this.rolesService
        .getItemId(item.id)
        .pipe(takeUntil(this.constants.destroy$))
        .subscribe(
          result => {
            loading.close();
            if (
              Object.keys(result).length > 0 &&
              Object.keys(result.data).length > 0
            ) {
              data.item = result.data;
              this.constants.message = this.appConfig.LANG.message.editSuccess;

              dialog = this.generalsService.modal(
                RolesEditComponent,
                data,
                'mat-modal-big'
              );

              this.finishDialog(dialog);
            } else {
              this.generalsService.notification(
                this.appConfig.NOTIFICATION.error,
                this.appConfig.LANG.roles.noRelEdit
              );
            }
          },
          error => {
            loading.close();
            this.generalsService.notification(
              this.appConfig.NOTIFICATION.error,
              this.generalsService.showError(error)
            );
          }
        );
    }
  }

  finishDialog(dialog: any) {
    dialog
      .afterClosed()
      .pipe(takeUntil(this.constants.destroy$))
      .subscribe((afterClosedResult: any) => {
        this.generalsService.afterClosed(
          this,
          afterClosedResult,
          this.constants.message
        );
      });
  }

  delete(rol: RolSingle) {
    const dialog = this.generalsService.alert(
      this.appConfig.LANG.roles.confirmDelete.replace('{ROL_NAME}', rol.name)
    );

    dialog
      .afterClosed()
      .pipe(takeUntil(this.constants.destroy$))
      .subscribe((result: any) => {
        if (result === 200) {
          const loading = this.generalsService.loading();
          this.rolesService
            .delete(rol.id)
            .pipe(takeUntil(this.constants.destroy$))
            .subscribe(
              () => {
                loading.close();
                this.generalsService.notification(
                  this.appConfig.NOTIFICATION.success,
                  this.appConfig.LANG.message.deleteRol.replace(
                    '{ROL_NAME}',
                    rol.name
                  )
                );
                this.reload();
              },
              (error: any) => {
                this.generalsService.notification(
                  this.appConfig.NOTIFICATION.error,
                  this.generalsService.showError(error)
                );
                loading.close();
              }
            );
        }
      });
  }

  filterData(event: any) {
    this.generalsService.applyFilter(this.dataSource, event);
    this.constants.filter = this.dataSource.filteredData;
  }
}
